import {
  viewSpecificControllerListCustomerPaymentsQueryOptions,
  customerControllerGetCustomerPaymentMethodsQueryOptions,
} from 'korint-sdk';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@folio/components/ui';
import { formatDate, formatPrice } from '@folio/lib';
import type { InvoiceGroup } from './InvoiceGroupRow';
import { InvoiceTimeline } from './InvoiceTimeline';
import { cva } from 'class-variance-authority';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { PlannedRecoveryTask } from '@folio/hooks/usePlannedRecoveryTasks';
import { Text } from '@folio/components/ui/text';
import { Stack } from '@folio/components/ui/flex';

interface InvoiceDetailsDrawerProps {
  group: InvoiceGroup;
  customerId: string;
  plannedRetries: PlannedRecoveryTask[];
  open: boolean;
  onClose: () => void;
}

const statusBadgeVariants = cva('px-2 py-1 rounded-md font-semibold text-center text-xs', {
  variants: {
    status: {
      DRAFTED: 'bg-gray-100 text-gray-700',
      ISSUED: 'bg-blue-100 text-blue-700',
      PAID: 'bg-green-100 text-green-700',
      FINALIZED: 'bg-purple-100 text-purple-700',
      CREDIT_FINALIZED: 'bg-orange-100 text-orange-700',
      PAYMENT_IN_PROGRESS: 'bg-yellow-100 text-yellow-700',
      PAYMENT_FAILED: 'bg-red-100 text-red-700',
      RECONCILED: 'bg-orange-100 text-orange-700',
      WRITTEN_OFF: 'bg-orange-100 text-orange-700',
      CANCELLED: 'bg-orange-100 text-orange-700',
      PAYMENT_DISPUTED: 'bg-red-100 text-red-700',
      REFUND_NOT_HANDLED: 'bg-red-100 text-red-700',
      REFUND_IN_PROGRESS: 'bg-yellow-100 text-yellow-700',
      PARTIALLY_FINALIZED: 'bg-red-100 text-red-700',
    },
  },
});

export function InvoiceDetailsDrawer({
  group,
  customerId,
  plannedRetries,
  open,
  onClose,
}: InvoiceDetailsDrawerProps) {
  const { t } = useTranslation();

  const invoice = group.displayInvoice;

  const { data: paymentsData, isLoading: isLoadingPayments } = useQuery({
    ...viewSpecificControllerListCustomerPaymentsQueryOptions(customerId),
    enabled: !!customerId && open,
  });

  const { data: paymentMethodsData } = useQuery({
    ...customerControllerGetCustomerPaymentMethodsQueryOptions(customerId),
    enabled: !!customerId && open,
  });

  const invoicePayments = useMemo(() => {
    if (!paymentsData?.data) return [];
    const invoiceIds = [group.originalInvoice?.id, group.creditInvoice?.id, invoice.id].filter(
      Boolean,
    );
    return paymentsData.data
      .filter(payment => {
        const invoiceIdMetadata = payment.metadata?.find(m => m.key === 'invoiceIds');
        if (!invoiceIdMetadata?.value) return false;
        return invoiceIdMetadata.value.split(',').some(id => invoiceIds.includes(id.trim()));
      })
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  }, [paymentsData, invoice.id, group.originalInvoice?.id, group.creditInvoice?.id]);

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) onClose();
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent className="!w-full sm:!w-[40vw] sm:min-w-[500px] !max-w-none overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            {t('invoices.drawer.title')} {invoice.externalId || invoice.id}
          </SheetTitle>
        </SheetHeader>

        <Stack gap={6} className="mt-6">
          {/* Basic Details Grid */}
          <div className="grid grid-cols-2 gap-4">
            <Stack gap={1}>
              <Text inline size="sm" tone="muted">
                {t('invoices.drawer.number')}
              </Text>
              <Text weight="medium">{invoice.externalId || invoice.id}</Text>
            </Stack>
            <Stack gap={1}>
              <Text inline size="sm" tone="muted">
                {t('invoices.drawer.amountWithTaxes')}
              </Text>
              <Text weight="medium">{formatPrice(invoice.total.amountWithFeesAndTaxes)}</Text>
            </Stack>
            <Stack gap={1}>
              <Text inline size="sm" tone="muted">
                {t('invoices.drawer.coveredPeriod')}
              </Text>
              <Text weight="medium">
                {formatDate(new Date(invoice.period.startedAt))} -{' '}
                {formatDate(new Date(invoice.period.endedAt))}
              </Text>
            </Stack>
            <Stack gap={1}>
              <Text inline size="sm" tone="muted">
                {t('invoices.drawer.status')}
              </Text>
              <div>
                <span
                  className={statusBadgeVariants({
                    status: invoice.status,
                  })}
                >
                  {t(`invoices.status.${invoice.status}`)}
                </span>
              </div>
            </Stack>
            {invoice.manualPayment && (
              <Stack gap={1}>
                <Text inline size="sm" tone="muted">
                  {t('invoices.drawer.manualPayment')}
                </Text>
                <Text weight="medium">
                  {t(`invoices.manualMark.method.${invoice.manualPayment.method}`)} -{' '}
                  {formatDate(new Date(invoice.manualPayment.appliedAt))}
                </Text>
              </Stack>
            )}
          </div>

          <InvoiceTimeline
            displayInvoice={invoice}
            originalInvoice={group.originalInvoice}
            creditInvoice={group.creditInvoice}
            invoicePayments={invoicePayments}
            paymentMethods={paymentMethodsData?.data || []}
            plannedRetries={plannedRetries}
            isLoading={isLoadingPayments}
          />
        </Stack>
      </SheetContent>
    </Sheet>
  );
}
